/**
 * Parses HTTP/client errors from privateRequest and axios.
 * @param {unknown} err
 * @returns {{ status: number|null, errorMessage: string }}
 */
export const extractHttpErrorInfo = err => {
  if (err?.response) {
    const status = err.response.status;
    const errorMessage =
      err.response.data?.message ||
      err.response.data?.error ||
      JSON.stringify(err.response.data) ||
      err.message ||
      "An error occurred";
    return {status, errorMessage};
  }

  if (err?.message) {
    return {status: null, errorMessage: err.message};
  }

  if (typeof err === "string") {
    return {status: null, errorMessage: err};
  }

  return {status: null, errorMessage: "An unknown error occurred"};
};

/**
 * Whether a failed request should end the user session (global logout).
 * 400 responses with "Token is not valid" are excluded — BO routes return those
 * for normal users and must fall back without clearing the session cookie.
 *
 * @param {unknown} err
 * @param {number|null} status
 * @param {string} errorMessage
 * @returns {boolean}
 */
export const shouldLogoutForSessionError = (err, status, errorMessage) => {
  if (errorMessage === "networkError" || errorMessage === "internalError") {
    return false;
  }

  if (errorMessage === "sessionExpired" || err?.isSessionAuth === true) {
    return true;
  }

  if (status === 401) {
    return true;
  }

  if (status === 403 && errorMessage !== "comment_moderated") {
    return true;
  }

  const hasTokenMessage =
    errorMessage.includes("Token is not valid") ||
    errorMessage.includes("Invalid Token") ||
    errorMessage.includes("Expired Token");

  return hasTokenMessage && (status === 401 || status === 403);
};

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export const isSessionAuthError = err => {
  const {status, errorMessage} = extractHttpErrorInfo(err);
  return shouldLogoutForSessionError(err, status, errorMessage);
};
